'use client';

/**
 * Yarn sharing — single-yarn bundle build + import-as-inspiration.
 *
 * Mirrors `projectShare.ts` 1:1. A sender picks a yarn library row,
 * we build a `.kyarn` ZIP, encrypt it, upload to the relay, and hand
 * the recipient a short URL. On the recipient side, the bundle is
 * imported as an Inspiration row with `kind: 'shared-yarn'` — the
 * recipient's yarn / inventory / technique / project tables are
 * ABSOLUTELY NOT TOUCHED. Stock counts (`skeinCount`,
 * `partialSkein`, `partialGrams`) and all housekeeping fields
 * (`deletedAt`, Dexie `id`) are stripped from the bundle at export
 * time so "how many I own" is private.
 *
 * Wire format of `.kyarn`:
 *   manifest.json  → { version, format: 'kyarn', exportedAt, senderName?, yarn: KyarnSnapshot }
 *   blobs/<hash>   → raw bytes for every `blob:sha256-<hex>` reference
 *
 * The same parseProjectBundle/parseYarnBundle ZIP-bomb defenses apply:
 * enforce per-entry + total uncompressed caps before any
 * `file.async(...)` call.
 */

import JSZip from 'jszip';
import { db } from './db';
import { getBlob } from './blobStore';
import { collectBlobHashes, loadAndGuardZip, countBundleBlobs, ingestBundleBlobs } from './share/shareBundleCommon';
import type { Yarn, Inspiration, KyarnManifest, KyarnSnapshot } from './types';

export const KYARN_VERSION = 1;
export const KYARN_FORMAT = 'kyarn';

/** Build a `.kyarn` Blob for a yarn. Photos and colorPhoto always travel
 *  (they're already compressed). Stock counts are stripped. */
export async function buildYarnBundle(
  yarnId: number,
  opts: { senderName?: string } = {},
): Promise<Blob> {
  const yarn = await db.yarns.get(yarnId);
  if (!yarn) throw new Error(`yarn ${yarnId} not found`);
  if (yarn.deletedAt) throw new Error('refusing to export a deleted yarn');

  const snapshot = sanitizeYarnSnapshot(yarn);

  const manifest: KyarnManifest = {
    version: KYARN_VERSION,
    format: KYARN_FORMAT,
    exportedAt: new Date().toISOString(),
    senderName: opts.senderName?.trim() || undefined,
    yarn: snapshot,
  };

  const hashes = new Set<string>();
  collectBlobHashes(manifest, hashes);

  const zip = new JSZip();
  zip.file('manifest.json', JSON.stringify(manifest));
  const blobs = zip.folder('blobs');
  for (const hash of hashes) {
    const blob = await getBlob(hash);
    if (blob) blobs?.file(hash, blob);
  }
  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

/** Strip stock counts, Dexie housekeeping, and soft-delete markers.
 *  Keeps everything that describes the colorway + the sender's review
 *  and gauge data so the recipient gets a rich reference card. */
function sanitizeYarnSnapshot(y: Yarn): KyarnSnapshot {
  return {
    uuid: y.uuid,
    name: y.name,
    brand: y.brand,
    fiber: y.fiber,
    weight: y.weight,
    colorName: y.colorName,
    colorNumber: y.colorNumber,
    colorHex: y.colorHex,
    colorPhoto: y.colorPhoto,
    yardage: y.yardage,
    gramsPerSkein: y.gramsPerSkein,
    pricePerSkein: y.pricePerSkein,
    priceCurrency: y.priceCurrency,
    recommendedNeedles: y.recommendedNeedles,
    mfgGaugeStitches: y.mfgGaugeStitches,
    mfgGaugeRows: y.mfgGaugeRows,
    mfgGaugeNeedle: y.mfgGaugeNeedle,
    myGaugeStitches: y.myGaugeStitches,
    myGaugeRows: y.myGaugeRows,
    myGaugeNeedle: y.myGaugeNeedle,
    review: y.review,
    notes: y.notes,
    shopUrl: y.shopUrl,
    photo: y.photo,
    createdAt: y.createdAt instanceof Date ? y.createdAt.toISOString() : String(y.createdAt),
    // NOT travelling: skeinCount, partialSkein, partialGrams, id,
    // deletedAt. Those are inventory-private.
  };
}

// ---------- Parse ----------

export interface ParsedYarnBundle {
  manifest: KyarnManifest;
  blobCount: number;
  bundleBytes: number;
  zip: JSZip;
}

/** Validate + parse a `.kyarn` Blob. Throws on malformed input. Does
 *  NOT write to Dexie or the blob store. */
export async function parseYarnBundle(blob: Blob): Promise<ParsedYarnBundle> {
  const zip = await loadAndGuardZip(blob);

  const manifestFile = zip.file('manifest.json');
  if (!manifestFile) throw new Error('bundle missing manifest.json');
  const rawManifest = await manifestFile.async('string');
  const manifest = JSON.parse(rawManifest) as KyarnManifest;
  if (manifest.format !== KYARN_FORMAT) {
    throw new Error(`unknown bundle format: ${manifest.format}`);
  }
  if (manifest.version !== KYARN_VERSION) {
    throw new Error(`unsupported bundle version: ${manifest.version}`);
  }
  if (!manifest.yarn) throw new Error('bundle missing yarn data');
  return { manifest, blobCount: countBundleBlobs(zip), bundleBytes: blob.size, zip };
}

// ---------- Import ----------

export interface YarnImportResult {
  inspirationId: number;
  uuid: string;
  duplicateSkipped: boolean;
  photosIngested: number;
}

/** Import a parsed yarn bundle as a new Inspiration row with
 *  `kind: 'shared-yarn'`. Writes to `db.inspirations` and the blob
 *  store only — no other Dexie tables are touched. */
export async function importSharedYarnAsInspiration(
  parsed: ParsedYarnBundle,
  opts: { skipIfDuplicateSourceUuid?: boolean } = { skipIfDuplicateSourceUuid: true },
): Promise<YarnImportResult> {
  const { manifest, zip } = parsed;
  const sourceUuid = manifest.yarn.uuid ?? '';

  // Duplicate detection: if an Inspiration with the same
  // sharedYarn.sourceUuid already exists, skip by default.
  if (opts.skipIfDuplicateSourceUuid && sourceUuid) {
    const existing = (await db.inspirations.toArray()).find(
      i => i.kind === 'shared-yarn' && i.sharedYarn?.sourceUuid === sourceUuid && !i.deletedAt,
    );
    if (existing) {
      return {
        inspirationId: existing.id!,
        uuid: existing.uuid ?? '',
        duplicateSkipped: true,
        photosIngested: 0,
      };
    }
  }

  // Ingest every bundled blob into the local content-addressed store.
  const photosIngested = await ingestBundleBlobs(zip);

  const snap = manifest.yarn;
  // Derive Inspiration-shaped display metadata so the card renders on
  // the Inspiration grid natively. Title prefers brand + color so
  // sharing "Nepal · Tmavá oranžová" reads well in a list. Photo
  // prefers colorPhoto (close-up swatch) over the broader product
  // photo.
  const titleParts = [snap.name, snap.colorName].filter(Boolean);
  const colors: string[] = [];
  if (snap.colorHex) colors.push(snap.colorHex);
  const tags = [snap.brand, snap.weight, snap.fiber]
    .filter((s): s is string => Boolean(s?.trim()));

  const newUuid = crypto.randomUUID();
  const inspiration: Omit<Inspiration, 'id'> = {
    uuid: newUuid,
    kind: 'shared-yarn',
    title: titleParts.join(' · ') || snap.name,
    photo: snap.colorPhoto || snap.photo,
    colors,
    notes: snap.notes,
    tags,
    link: undefined,
    createdAt: new Date(),
    sharedYarn: {
      sourceUuid,
      senderName: manifest.senderName,
      importedAt: new Date(),
      snapshot: snap,
    },
  };

  let id: number;
  try {
    id = (await db.inspirations.add(inspiration as Inspiration)) as number;
  } catch (e) {
    throw new Error(
      `Could not save the shared yarn to your Inspiration library: ${(e as Error).message}`,
    );
  }

  return {
    inspirationId: id,
    uuid: newUuid,
    duplicateSkipped: false,
    photosIngested,
  };
}
